import React, { useEffect, useMemo, useRef, useState } from "react";

import { faTriangleExclamation } from "@performyard/fontawesome/pro-solid-svg-icons";
import { InputWrapper, PageBanner, Spinner, Tabs, Text, useWizard } from "@performyard/py-components";

import TemplateSelect, { type ReviewTemplateSummary } from "../../../../../../components/Inputs/SelectTemplate";
import ModalSelectForms from "../../../../../../components/Modals/ModalSelectForms";
import { FormTabPanel } from "./FormTabPanel";

import { FORM_TYPES, type FormType } from "./types";
import type { AddFormsStepData } from "./types";

import { useAllReviewForms } from "../../../../../../api/hooks/defs/forms";
import { useReviewTemplate } from "../../hooks/useReviewTemplate";
import { useAddFormsState } from "./useAddFormsState";

import type { SettingsData } from "../CycleSettingsStep";
import {
	NO_TEMPLATE_BANNER,
	STEP_ID,
	TAB_LABELS,
	TEMPLATE_NO_ACTIVE_FORMS_BANNER,
	TEMPLATE_REQUIRED_ERROR,
} from "./constants";
import { validateAddForms } from "./validateForms";

type AddFormsStepProps = {
	cycleStartDate: Date;
	/** Wizard parent shares this ref with us so its `beforeNext` always calls the latest validator. */
	validateRef: React.MutableRefObject<(data: unknown) => boolean>;
};

export const AddFormsStep = ({ cycleStartDate, validateRef }: AddFormsStepProps) => {
	const { wizardData, stepsWithErrors, currentStep, wizardId, markStepError, clearStepError } = useWizard();
	const cycleType = (wizardData["basic-info"] as SettingsData | undefined)?.cycleType ?? "manual";
	const allowEditing = cycleType !== "automated";

	const {
		state,
		setSelectedTemplate,
		applyTemplateAssignments,
		clearTemplate,
		addAdHocForms,
		updateForm,
		removeForm,
	} = useAddFormsState();
	const stepHasError = stepsWithErrors.includes(currentStep);

	const [activeTab, setActiveTab] = useState<FormType>("standard");
	const [isModalOpen, setIsModalOpen] = useState(false);

	// Guards against re-running applyTemplateAssignments on remount, which would
	// regenerate localIds and discard user edits on the template's forms.
	const appliedTemplateRef = useRef<string | null>(state.selectedTemplate?.id ?? null);

	const { data: templateData, isLoading: isTemplateLoading } = useReviewTemplate(state.selectedTemplate?.id ?? null);
	const { data: formsData } = useAllReviewForms();
	const formNamesById = useMemo(() => {
		const map = new Map<string, string>();
		formsData?.data?.forEach((f) => map.set(f.baseId, f.name));
		return map;
	}, [formsData]);

	// Skip soft-deleted forms — matches the legacy cycle-create UI.
	const activeAssignments = useMemo(() => {
		if (!templateData || !formsData) return null;
		return templateData.formAssignments.filter((a) => formNamesById.has(String(a.formBaseId)));
	}, [templateData, formsData, formNamesById]);

	const templateHasNoActiveForms =
		!!state.selectedTemplate && activeAssignments !== null && activeAssignments.length === 0;

	useEffect(() => {
		if (!templateData || !activeAssignments) return;
		const templateId = String(templateData._id);
		if (state.selectedTemplate?.id === templateId && appliedTemplateRef.current !== templateId) {
			appliedTemplateRef.current = templateId;
			applyTemplateAssignments(templateId, activeAssignments);
		}
	}, [templateData, activeAssignments, state.selectedTemplate?.id, applyTemplateAssignments]);

	// Wizard.performNavigation calls validateRef but doesn't mark the step on failure, so we do it here.
	useEffect(() => {
		validateRef.current = (data: unknown) => {
			const result = validateAddForms(data as AddFormsStepData | undefined);
			if (!result.valid || templateHasNoActiveForms) {
				// Submit each card so inline schema errors surface; cards on inactive tabs no-op.
				const allLocalIds = [
					...state.forms.standard.map((f) => f.localId),
					...state.forms["360"].map((f) => f.localId),
					...state.forms.external.map((f) => f.localId),
				];
				allLocalIds.forEach((localId) => {
					const formEl = document.getElementById(`${wizardId}-form-${localId}`) as HTMLFormElement | null;
					formEl?.requestSubmit();
				});
				markStepError(currentStep);
				return false;
			}
			clearStepError(currentStep);
			return true;
		};
	}, [validateRef, currentStep, markStepError, clearStepError, state.forms, wizardId, templateHasNoActiveForms]);

	const handleTemplateChange = (template: ReviewTemplateSummary | null) => {
		if (!template) {
			appliedTemplateRef.current = null;
			clearTemplate();
			return;
		}
		if (state.selectedTemplate?.id === template.id) return;
		appliedTemplateRef.current = null;
		setSelectedTemplate({ id: template.id, name: template.name });
	};

	const handleConfirmModal = (selectedFormIds: string[]) => {
		const seeds = selectedFormIds.map((id) => ({ formId: id, formName: formNamesById.get(id) ?? id }));
		addAdHocForms(activeTab, seeds, cycleStartDate);
		setIsModalOpen(false);
	};

	const templateValue = state.selectedTemplate ?? undefined;

	const showTemplateError = stepHasError && !state.selectedTemplate;

	const tabs = FORM_TYPES.map((type) => {
		const count = state.forms[type].length;
		const label = count > 0 ? `${TAB_LABELS[type]} (${count})` : TAB_LABELS[type];
		return {
			value: type,
			label,
			content:
				type === activeTab ? (
					<FormTabPanel<typeof type>
						formType={type}
						forms={state.forms[type]}
						cycleStartDate={cycleStartDate}
						allowEditing={allowEditing}
						wizardId={wizardId}
						onChangeForm={(localId, data) => updateForm(type, localId, data)}
						onRemoveForm={(localId) => removeForm(type, localId)}
						onOpenSelectForms={() => setIsModalOpen(true)}
					/>
				) : null,
		};
	});

	return (
		<div className="mx-auto flex w-full max-w-[640px] flex-col gap-4" data-testid={`${STEP_ID}-step`}>
			{stepHasError && !state.selectedTemplate && (
				<PageBanner
					color="red"
					mainTitle={NO_TEMPLATE_BANNER}
					icon={faTriangleExclamation}
					dangerouslySetClassName="!p-1.5"
				/>
			)}
			{stepHasError && templateHasNoActiveForms && (
				<PageBanner
					color="red"
					mainTitle={TEMPLATE_NO_ACTIVE_FORMS_BANNER}
					icon={faTriangleExclamation}
					dangerouslySetClassName="!p-1.5"
				/>
			)}

			<InputWrapper
				id={`${STEP_ID}-template-select`}
				label="Select a Review Template"
				disableLabelClick
				error={showTemplateError ? TEMPLATE_REQUIRED_ERROR : undefined}
			>
				<TemplateSelect value={templateValue} onChange={handleTemplateChange} isClearable={allowEditing} />
			</InputWrapper>

			{allowEditing && (
				<Text size="paragraph-2" weight="regular" color="neutral-7" aria-hidden="true">
					and/or
				</Text>
			)}

			{isTemplateLoading && (
				<div className="flex justify-center py-4">
					<Spinner size={24} />
				</div>
			)}

			{!isTemplateLoading && (
				<Tabs
					tabs={tabs}
					value={activeTab}
					onValueChange={(v) => setActiveTab(v as FormType)}
					color="neutral"
					size="large"
				/>
			)}

			<ModalSelectForms
				isOpen={isModalOpen}
				onClose={() => setIsModalOpen(false)}
				onConfirm={handleConfirmModal}
				title="Select Review Forms"
			/>
		</div>
	);
};
